import type { Palette } from "@mui/material/styles";
import { FormatType } from "../Alert/Filter/FilterType";
import {
  GridCellParams,
  GridRowModel,
  GridRowSelectionModel,
} from "@mui/x-data-grid-pro";
import { ComponentType, ReactNode } from "react";
import { IntlShape } from "react-intl";
import { DateTimeFormatOptions } from "luxon";
import { GridAlignment, GridSortDirection } from "@mui/x-data-grid";
import { InputProps } from "@mui/material/Input";
import { DateView } from "@mui/x-date-pickers";
import { Flags, Roles } from "../AuthProvider";
import { Industry } from "../Industry";
import { GenericObject } from "../services/Price";
import { Breakpoint, SxProps } from "@mui/material";

export enum ConfigurationPageType {
  // composant pour page simple
  TABLE = "table",
  // page avec plusieurs tabulation
  TABS = "tabs",
  // connectivity/connectors page
  CONNECTIVITY = "connectivity",
  // arbitrary React component
  CUSTOM = "custom",
  // construction modèles (courbes de pickup)
  MODEL_CONSTRUCTION = "model_construction",
}

interface FieldOptions extends EditFormDetail {
  flex: string;
  key: string;
  required?: boolean;
  readonly?: boolean;
  fields?: FieldOptions[];
  columns?: FieldOptions[];
}

// TODO: Check that this is correct with the other combination
// Allow to group variables as all optional or all mandatory
// type AllOrNothing<T> = T | Partial<Record<keyof T, undefined>>;

// This is a bit verbose and requires to declare all the boolean
// but we cannot miss a boolean true without the options
interface RequiredCopyOptions {
  copyable: true;
  copyOptions: EditFormDetail[];
}

interface OptionalCopyOptions {
  copyable: false;
  copyOptions?: EditFormDetail[];
}

interface RequiredCreateOptions {
  creatable: true;
  createOptions: FieldOptions[];
}

interface OptionalCreateOptions {
  creatable: false;
  createOptions?: FieldOptions[];
}

interface RequiredEditOptions {
  editable: true | ((row: GridRowModel) => boolean);
  editOptions: FieldOptions[];
}

interface OptionalEditOptions {
  editable: false;
  editOptions?: FieldOptions[];
}

interface rankableOptions {
  // Name of api ordering column/param
  column: "order" | "rank";
  // Filter to watch
  watchLookup?: string;
}

export interface additionalFormsType {
  dynamicPath?: boolean;
  name: string;
  label: string;
  labelButton: string;
  path: string;
  formFields: FormDetailsType;
  options: FieldOptions[];
}

export type ScreenSizeType = "medium" | "large";

// Per-row delete state. Returning `{ disabled, reason }` keeps the
// trash icon visible (preserves Action-column alignment) but renders
// it disabled with a tooltip explaining why. `reason` is a translation
// key — falls back to its raw id if no translation is present.
export type DeletableResult = boolean | { disabled: true; reason: string };

interface BaseOptions {
  formFields: FormDetailsType;
  deletable?: boolean | ((row: GridRowModel) => DeletableResult);
  /**
   * Route deletion through a `DeleteRequest` instead of `DELETE <path>/<id>/`.
   * Required for properties and uh_cats: the generic REST delete has no notion
   * of their dependency graph, cascades some relations with no backup and hits
   * a foreign key on the rest. The request flow backs everything up first and
   * keeps it restorable from the Trash page for the retention period.
   */
  deleteTarget?: "property" | "uh_cat";
  hideMassDelete?: boolean;
  editSelectedOptions?: EditFormDetail[];
  rankable?: rankableOptions;
  /**
   * Show the API validation errors inside the create/edit modal instead of only
   * flashing the generic failure snackbar. Enable it on tables whose backend
   * rejects submissions with rules the form cannot express on its own.
   */
  displayFormError?: boolean;
  // formSize?: ScreenSizeType;
  additionalForms?: additionalFormsType[];
  formSize?: Breakpoint;
  /**
   * More submit buttons beside "Save" in the create and edit dialogs (not mass
   * edit nor sub-forms). Each sends the same form, with the same method, to
   * `<model.path>/<action>/` on creation and `<model.path>/<id>/<action>/` on
   * edit.
   */
  extraSubmits?: ExtraSubmit[];
}

export type ExtraSubmit = {
  // The detail/list route segment receiving the form.
  action: string;
  // Intl key of the button.
  label: string;
};

/** What a `customForm` receives from the dialog it is rendered in. */
export type CustomFormProps = {
  dialogFormType: ConfigurationDialogFormType;
  /** The row being edited, absent on creation. */
  row?: GridRowModel;
  onClose: () => void;
  /** Refresh the underlying table, after the custom form saved something. */
  refetch?: () => void;
  /**
   * Ask the dialog to resize. A form that opens on one question and ends on a
   * page of answers can start narrow and widen once it has something to show.
   */
  onWidthChange?: (width: Breakpoint) => void;
};

/**
 * A table whose create and edit dialogs are a component rather than the generic
 * form builder, which owns its own footer. The escape hatch for a feature whose
 * interaction is not "fill fields, press submit" — the group quoting studio
 * recomputes live and has no submit step at all. Such a table declares no
 * create or edit options: there is no generic form to configure.
 */
interface CustomFormOptions {
  customForm: ComponentType<CustomFormProps>;
  creatable?: boolean;
  editable?: boolean | ((row: GridRowModel) => boolean);
  createOptions?: never;
  editOptions?: never;
}

interface BuiltFormOptions {
  customForm?: never;
}

export type TableOptions = BaseOptions &
  (RequiredCopyOptions | OptionalCopyOptions) &
  (
    | CustomFormOptions
    | (BuiltFormOptions &
        (RequiredCreateOptions | OptionalCreateOptions) &
        (RequiredEditOptions | OptionalEditOptions))
  );

type ApiParamsType = {
  page_size: number;
  ordering: string;
  property?: {
    path: string;
  };
  id?: {
    path: string;
  };
  /** The row's own id, for a list route that needs the record it is being asked from. */
  contract?: {
    path: string;
  };
  is_property_reference?: boolean;
  is_uh_type_reference?: boolean;
  uh_type_nandc?: {
    path: string;
  };
  // Specify a range of values for id.
  // Eg. for a range of kpis `id__range: "168,172"`
  id__range?: string;
  code__contains?: string;
  competitor_uh_cat?: {
    path: string;
  };
  is_staff?: boolean;
  // Grid/Core Mapping
  client_property_code?: {
    path: string;
  };
  client_uh_cat_code?: {
    path: string;
  };
  grid_type__isnull?: boolean;
};

export type ApiType = {
  api: string;
  caseOfValueIs: string;
  // Use to determine which param is send to backend (default id)
  dataKey?: string;
  labelKey: string;
  apiParams?: ApiParamsType;
  defaultValue?: string;
};

type MultiApitype = {
  // Field to watch
  watchFilter: string;
  /**
   * apisFieldMap: Used to generate the initial field values.
   * Acts as a bridge between the watched field value (watchFilter)
   * and the key to read in the API response.
   *
   * The "key" represents the watched field value,
   * and the "value" represents the property name to extract from the API data.
   */
  apisFieldMap: Record<string, string | null>;
  // Api to load according watching field value
  apis: ApiType[];
};

export type editorInputsType = {
  // Use cursorInside to true, if you want to place cursor inside a function (ex: floor(|))
  cursorInside?: boolean;
  // Use this to identify this input as a variable, if not it will be use like normal operator and occurs issues
  isVariable?: boolean;
  key: string;
  // Label is the label write on the button
  label: string;
  placeholder?: string;
  // Use when need to add a tooltip to input
  tooltip?: string;
  // Use this to prefill input calculator with the value of another input/data
  type?: "autofill";
  // Use this to render error in input if value not used
  required?: boolean;
  // Decide if label need to be translated
  translate?: boolean;
  // Value is the value use in input
  value: string;
  // (TODO: increase this) Use to display a warning message ath bottom of caculator input
  warning?: {
    // Label of the warning
    label: string;
    // The value which trigger the warning
    value: number | string | null;
    // The field value to watch
    watchField: string;
  };
  // (TODO: increase this) Use this to display a warning when the value of a parmas is egal to something
  watchField?: WatchFieldType | string;
};

interface editorInputsSimulationType extends editorInputsType {
  defaultValue?: number;
  // Use this type to fill input according value of another one, this will also disable the input
  type?: "autofill";
  labelKey?: string;
  api?: string;
  apiParams?: ApiParamsType;
  watchField?: WatchFieldType | string;
}

export type WatchFieldType = {
  paramsKey: string;
  paramsValue: string;
  watchFieldPath: string;
  optional?: boolean;
}[];

type InputTypeType =
  | "string"
  | "number"
  | "integer"
  | "email"
  | "percent"
  | "negativeInteger"
  | "currency";

// TODO: add a generic type for create, edit, copy
export type BasicEditFormDetails = {
  api?: string;
  apiParams?: ApiParamsType;
  // Use when filter need to load multi api according to other field
  column?: string;
  // Use this to disable or enable current field depending checkbox field value given
  enableField?: string;
  filterQueryKey?: string;
  flex: string;
  // Use when need to add select all option in selector (This behavior is similar to select all option added for multy row edit mode)
  hasCheckAllOption?: boolean;
  // When true, auto-select all options if the field value is empty (e.g. on creation or legacy rows with no selection)
  selectAllByDefault?: boolean;
  includedIndustries?: Industry[];
  // Use inputType when you need to specify value type of input, if undefined type is string by default
  inputType?: InputTypeType;
  // Use this to keep specific data from api and store it in options value
  keepApiData?: string[];
  key: string;
  label: string;
  labelKey?: string;
  // Limit number values selected
  limitValuesSelected?: number;
  readonly?: boolean;
  required?: boolean;
  // Rendered and validated like any field, but never sent to the backend
  virtual?: boolean;
  // Styles of the field's slot in the form row (spacing, alignment).
  wrapperSx?: SxProps;
  // Allow to fill input with a value from another input field (ex: fill date week input with date input value)
  initialValueFromRow?: (row: GridRowModel) => unknown;
  /**
   * relatedInputAction aims to replace "enableField", "watchField", "watchFilledField" and "watchFieldToFill" params.
   * Use this on the dependent input. In other words, this parameter should be placed on the input that needs to be acted upon in relation to the target input.
   *
   * • action: You can disable, hide or prefill the curent input depending of the targeted input.
   * • relatedInput :
   *   - If you just want to get a values from form, just add the key value name (ex: first_name)
   *   - If you need more complexe action:
   *     - paramsKey : set the key params name to send to api in payload
   *     - paramsValue : set the path of value send with paramsKey (exp: if you want id from object {id: "myid", label: "hello"} set "id" )
   *     - watchFieldPath : set key field params to watch (exp: if you want to look field with 'key: "uh_cat.property"', set "uh_cat.property"')
   * • equals: trigger the action only if the value is equal to the value set in this param. If not set, action wil be triggered if the input is filled
   */
  relatedInputAction?: {
    action: "disable" | "enable" | "hide" | "show" | "prefill";
    relatedInput: WatchFieldType | string;
    equals?: string | number | boolean;
  };
  // Use secondary key when input type is StaticSelect, it would need to get the good data value for the selector in component StaticSelector
  secondaryKey?: StaticSelector;
  // Use this if you want to precise if the input must not be send to back if not filled
  skipIfEmpty?: boolean;
  subCreationForm?: ConfigurationPageTable | ConfigurationPageTables;
  subLabel?: string;
  visible?: boolean;
  // Hides the field for some rows (e.g. a flag that only makes sense on a past
  // date); its value still travels with the submit. Static `visible` wins.
  hiddenIf?: (row: GridRowModel | undefined, values: GenericObject) => boolean;
  // Disables the field for some rows or form states; a checkbox is also
  // unticked. `disabledHint` (i18n key) says why, under the field.
  disabledIf?: (
    row: GridRowModel | undefined,
    values: GenericObject
  ) => boolean;
  disabledHint?: string;
  // Use when need to add a tooltip to input
  tooltip?: string;
  // Use for date picker input, allow to choose calendar view (see: https://mui.com/x/react-date-pickers/date-picker/#views)
  views?: DateView[];
  /**
   * Use if you want watch other field to load differents datas according other field value (ex: properties vs uh cat)
   *  - watchFieldPath : set key field params to watch (exp: if you want to look field with 'key: "uh_cat.property"', set "uh_cat.property"')
   *  - paramsKey : set the key params name to send to api in payload
   *  - paramsValue : set the path of value send with paramsKey (exp: if you want id from object {id: "myid", label: "hello"} set "id" )
   * Don't forget to use "watchedBy" param.
   * If you want to get a values from form, just add the key value name (ex: first_name)
   */
  watchField?: WatchFieldType | string;
  /**
  /**
   * Use if you add a "watchField" params in other field to target this field. Use key params.
   * (exp: if field 'key: "uh_cat"' has 'watchField: "property"' to looked for field 'key: "property"', put 'watchBy: "uh_cat"' in 'key: "property"' field)
   */
  watchedBy?: string | string[];
  /**
   * Use to look if other input are filled. If filled, connected input with "watchedBy" and "watchField" are disabled
   */
  watchFilledField?: string;
  /**
   * Use to look on a input and fill current field with value
   */
  watchFieldToFill?: string;
  /**
   * Use for prefill input according lookup indicated
   */
  watchLookup?: string;
  /**
   * Use if you want to pass additional props to the TextField (Input) component, like prefix.
   */
  inputProps?: Partial<InputProps>;
  /**
   * If you want to "only" accept specific values in the input field.
   * @example If you want to only accept numbers.
   */
  inputReplacer?: (value: string | undefined) => string | undefined;
  maxFromApi?: {
    api: string;
    watchField: WatchFieldType;
    responseKey?: string;
    errorKey?: string;
  };
  /**
   * If a field is not set and has default value, set this value on form validation
   */
  defaultValue?: string | boolean | null;
  // Use dataKey to get the correct value from object data send to submit function
  // (e.g: {id: 1, label: "MyLabel"} => by default we use id but in some case we want label )
  dataKey?: string;
  /**
   * Optional render slot below the field. Use for inline guidance that
   * depends on the wider form state — typically a component that uses
   * `useFormikContext()` to react to other field values (e.g. an API-
   * backed "Suggested: X · Apply" hint). Kept generic so it's reusable
   * across config pages.
   */
  helperRender?: () => React.ReactNode;

  /** Text attributes
   *
   */
  textAlign?: "left" | "center" | "right";
  textStyle?: "normal" | "italic" | "oblique" | "inherit";

  /** Toggle attributes
   *
   * `toggleConditions` is a string comma-separated list of keys to check
   *   if the toggle should be enabled or not.
   * `toggleState` force the toggle to be open or closed.
   * `toggleOnLabel` is the label displayed when the toggle is on.
   * `toggleOnTooltip` is the tooltip displayed when the toggle is on.
   * `toggleParent` bind a widget to the parent (toggle) element.
   */
  toggleConditions?: string;
  toggleState?: boolean;
  toggleOnLabel?: string;
  toggleOnTooltip?: string;
  toggleParent?: string; // For sub elements
};

export interface SelectFilterItemSortedFormDetails
  extends BasicEditFormDetails {
  // Use this to inform if datas is object list or array
  apiResultsType?: ApiResultsType;
  type: ConfigurationFormType.Select;
  // Use this to keep specific data from api and store it in options value
  keepApiData?: string[];
  // Use freeSolo to use freeSolo mode from Mui
  freeSolo?: boolean;
  // Use dataKey to get the correct value from object data send to submit function
  // (e.g: {id: 1, label: "MyLabel"} => by default we use id but in some case we want label )
  dataKey?: string;
  /**
   * Called when the user picks a value, after Formik has stored it. Escape hatch
   * for page-specific side effects that the declarative config cannot express —
   * typically prefilling sibling fields from values carried by the edited row.
   * Not called when the selection is cleared.
   */
  onValueChange?: (helpers: {
    row?: GridRowModel;
    setFieldValue: (field: string, value: unknown) => void;
  }) => void;
  // Colour tile before each option and the selected value, read from this
  // key of the API item (list it in `keepApiData`).
  renderOption?: { type: "color"; key: string };
}

// A value the user does not pick but that follows other fields (e.g. the model
// matching a family, a hotel and a day): queried as soon as `lookupParams`
// returns something, written into `writeTo.field`. Always virtual: the field
// itself only holds the found item, and blocks the submit while there is none.
export interface RemoteLookupFormDetails extends BasicEditFormDetails {
  type: ConfigurationFormType.RemoteLookup;
  api: string;
  lookupParams: (
    values: GenericObject,
    row?: GridRowModel
  ) => GenericObject | null;
  // Picks the item among the results; the first one by default.
  pick?: (
    items: Item[],
    values: GenericObject,
    row?: GridRowModel
  ) => Item | null;
  writeTo: { field: string; value: (item: Item | null) => unknown };
  // Shown after the label when an item was found.
  render?: (item: Item, intl: IntlShape) => ReactNode;
  // Error shown, and blocking the form, when nothing matches.
  notFoundLabel: string;
}

export interface ChartPoint {
  leadTime: number;
  value: number | null;
}

// One curve of a form chart: where to fetch it from, given the form values,
// and how to read the reply. A null url leaves the curve out (e.g. nothing
// assigned yet); a 404 or an empty reply keeps its legend entry, greyed out.
// Chart tokens that are a colour, leaving out the nested groups (gauge, series).
type ChartPalette = Palette["chart"];
export type ChartColorToken = {
  [K in keyof ChartPalette]: ChartPalette[K] extends string ? K : never;
}[keyof ChartPalette];

export interface ChartSeriesDetails {
  key: string;
  label: string;
  url: (
    values: GenericObject,
    row: GridRowModel | undefined,
    scope: number | null
  ) => string | null;
  points: (data: unknown) => ChartPoint[];
  // Theme chart token of the stroke; the model colour by default.
  color?: ChartColorToken;
  dashArray?: string;
  // Round caps turn a dash pattern into dots.
  lineCap?: "butt" | "round" | "square";
  strokeWidth?: number;
  // Shown under the greyed legend entry: `emptyLabel` when the API answers
  // with no point (the curve is not computed), `notFoundLabel` on a 404 (no
  // curve for the scope picked in the header).
  emptyLabel?: string;
  notFoundLabel?: string;
}

// Optional scope selector in the chart header (e.g. a UH type): its options
// come from an API, the chosen id is handed to every series' `url`.
export interface ChartScopeDetails {
  // i18n key of the control's accessible name.
  label: string;
  // i18n key of the unscoped option.
  allLabel: string;
  api: string;
  lookupParams: (
    values: GenericObject,
    row?: GridRowModel
  ) => GenericObject | null;
  options: (items: Item[]) => { id: number; label: string }[];
}

// Read-only lead-time chart drawn from the other fields: never submitted.
export interface ChartFormDetails extends BasicEditFormDetails {
  type: ConfigurationFormType.Chart;
  chart: "leadTimeCurve";
  series: ChartSeriesDetails[];
  scope?: ChartScopeDetails;
  // Occupation day the hover badge dates each lead time from.
  date: (values: GenericObject, row?: GridRowModel) => string;
  height?: number;
}

/**
 * Use in case of DateRangePicker, need to know name paramater to send to backend
 */
export interface DateRangePickerFormDetails extends BasicEditFormDetails {
  type: ConfigurationFormType.DateRangePicker;
  startDateField: string;
  endDateField: string;
  startDateLabel: string;
  startDateTooltip?: string;
  endDateLabel: string;
  endDateTooltip?: string;
  disablePast?: boolean;
  lockPast?: boolean;
}

export interface EditFormDetails extends BasicEditFormDetails {
  type: Exclude<
    ConfigurationFormType,
    | ConfigurationFormType.MultiSelect
    | ConfigurationFormType.DateRangePicker
    | ConfigurationFormType.Select
    | ConfigurationFormType.FieldGroup
    | ConfigurationFormType.RadioGroup
    | ConfigurationFormType.RemoteLookup
    | ConfigurationFormType.Chart
    | ConfigurationFormType.PreviewBlock
    | ConfigurationFormType.ErrorMessage
    | ConfigurationFormType.Warning
  >;
}

export interface RadioGroupFormDetails extends BasicEditFormDetails {
  type: ConfigurationFormType.RadioGroup;
  options: {
    value: string;
    label: string;
    description?: string;
  }[];
  defaultValue?: string;
}

export interface MultiApiTypeFormDetails extends BasicEditFormDetails {
  type: ConfigurationFormType.MultiSelect;
  // Needed when "type" is "ConfigurationFormType.MultiSelect", used to load multi api in single select according other field input value
  multiApi: MultiApitype;
  // if true, multiple element can be choosen
  multiple?: boolean;
  // if multiple is true, decide how many tag can be displayed
  maxTag?: number;
  // If true, the size of input is controlled to avoid increasing its size in case of multiple selections
  // therefore only one tag will be displayed
  controlledInputSize?: boolean;
  // If true, null value will be interpreted as "All" value
  interpretNullAsAll?: boolean;
}

export type StatusTextType = "warning" | "info" | "error" | "nancie";

export interface TextFormDetails extends BasicEditFormDetails {
  type: ConfigurationFormType.Text;
  // Use this if you want to add an icon before text, icon will be define according status type
  icon?: boolean;
  // Use this if you want to add a separator line before the text
  separator?: boolean;
  // Define text styles
  status?: StatusTextType;
  sx?: SxProps;
  className?: string[];
  // Optional params for conditional display
  conditionalTextRenderer?: {
    // Show this text only if the current user has a last_successful_batch
    showIfBatchRan?: boolean;
  };
}

export interface PreviewsBlockFormDetails extends BasicEditFormDetails {
  type: ConfigurationFormType.PreviewBlock;
  labelError: string;
  // List of all value to display
  datasPreview: Array<{
    key: string;
    label: string;
    inputType?: InputTypeType;
  }>;
  // Render the block even when its bound value is falsy (false / 0 / null).
  // Required for blocks bound to a boolean status field where the falsy state
  // is still meaningful (e.g. a capacity-failed warning).
  showWhenFalsy?: boolean;
  // Optional ICU narrative key. When set, the formatted message is rendered
  // above the data rows, with placeholders bound to keys in datasPreview.
  narrativeKey?: string;
  // Fallback narrative key shown when one or more keys listed here are missing
  // from the row (e.g. forecast inputs unavailable for the requested dates).
  narrativeFallbackKey?: string;
  narrativeFallbackOnMissingKeys?: string[];
  // Optional warning rendered prominently when the bound value is falsy.
  warningKey?: string;
}

export interface FieldGroupFormDetails extends BasicEditFormDetails {
  type: ConfigurationFormType.FieldGroup;
  key: string;
  label: string;
  buttonLabel: string | null;
  // How display the remove button => "inline" = end of inputs line; button = add button at top right of container
  removeButtonDisplay: "button" | "inline";
  // Amount of field group allowed (null = Infinity)
  limit: number | null;
  visible?: boolean;
  flex: string;
  fields: FormDetailsFieldType[];
  // Field keys (dot-notation) to copy from the first existing entry when adding a new one
  prefillFields?: string[];
}

export interface FieldGroupColumnFormDetailsColumnsType {
  flex: string;
  key: string;
  columnLabel: string;
  columnTooltip?: string;
  fields: FormDetailsFieldType[];
}

export interface FieldGroupColumnFormDetails extends BasicEditFormDetails {
  buttonLabel: string | null;
  columns: Omit<FieldGroupColumnFormDetailsColumnsType, "flex">[];
  flex: string;
  key: string;
  label: string;
  // Amount of field group allowed (null = Infinity)
  limit: number | null;
  limitMin?: number;
  limitDefault?: number; // initial item count when no existing data; falls back to limitMin
  // How display the remove button => "inline" = end of inputs line; button = add button at top right of container
  removeButtonDisplay: "button" | "inline";
  type: ConfigurationFormType.FieldGroupColumn;
  visible?: boolean;
  // Limit the value on input to 100 to show error with Yup (use in case of connecting room feature)
  sumPercentLimit?: string | string[];
  // Keys to auto-redistribute equally (e.g. ["ratio_ca", "ratio_pax"]) when rows are added/removed
  autoFillPercent?: string[];
}

export interface FieldGroupColumnFormDetailsFilled
  extends FieldGroupColumnFormDetails {
  columns: FieldGroupColumnFormDetailsColumnsType[];
}

export interface ErrorMessageFormDetails extends BasicEditFormDetails {
  type: ConfigurationFormType.ErrorMessage;
  // Use this if you want to implement dynamic translation by injecting the value of the key of dynamicLabelValueKey in translation
  dynamicLabelValueKey?: string;
}

export interface CalculEditorFormDetails extends BasicEditFormDetails {
  type: ConfigurationFormType.CalculEditor;
  // Use this to put a max char limit to input
  inputMaxLength?: number;
  placeholder?: string;
  // Use this to display buttons in calculator
  editorInputs?: editorInputsType[];
}

export interface CalculEditorSimulationFormDetails
  extends BasicEditFormDetails {
  type: ConfigurationFormType.CalculEditorSimulation;
  // Use to identify input to customize
  editorInputs?: editorInputsSimulationType[];
}

export interface WarningFormDetails extends BasicEditFormDetails {
  type: ConfigurationFormType.Warning;
  /** Component that renders the warning content. Receives the edited `row`
   *  (whose `id` is not in the formik values) and reads any other context it
   *  needs (e.g. formik values, react-query) internally. */
  component: React.ComponentType<{ row?: GridRowModel }>;
}

export interface StaticSelectFormDetails extends BasicEditFormDetails {
  type: ConfigurationFormType.StaticSelect;
  // If true, multiple element can be choosen
  multiple?: boolean;
  // If multiple is true, decide how many tag can be displayed
  maxTag?: number;
  // If true, the size of input is controlled to avoid increasing its size in case of multiple selections
  // therefore only one tag will be displayed
  controlledInputSize?: boolean;
  // If true, null value will be interpreted as "All" value
  interpretNullAsAll?: boolean;
}

export type FormDetailsType = (
  | Omit<EditFormDetails, "flex">
  | Omit<MultiApiTypeFormDetails, "flex">
  | Omit<DateRangePickerFormDetails, "flex" | "label">
  | Omit<SelectFilterItemSortedFormDetails, "flex">
  | Omit<TextFormDetails, "flex">
  | Omit<FieldGroupFormDetails, "flex">
  | Omit<PreviewsBlockFormDetails, "flex">
  | Omit<ErrorMessageFormDetails, "flex">
  | Omit<CalculEditorFormDetails, "flex">
  | Omit<CalculEditorSimulationFormDetails, "flex">
  | Omit<StaticSelectFormDetails, "flex">
  | Omit<FieldGroupColumnFormDetails, "flex">
  | Omit<WarningFormDetails, "flex">
  | Omit<RadioGroupFormDetails, "flex">
  | Omit<RemoteLookupFormDetails, "flex">
  | Omit<ChartFormDetails, "flex">
)[];

export type FormDetailsFieldType =
  | Omit<EditFormDetails, "flex">
  | Omit<MultiApiTypeFormDetails, "flex">
  | Omit<DateRangePickerFormDetails, "flex" | "label">
  | Omit<SelectFilterItemSortedFormDetails, "flex">
  | Omit<TextFormDetails, "flex">
  | Omit<FieldGroupFormDetails, "flex">
  | Omit<PreviewsBlockFormDetails, "flex">
  | Omit<ErrorMessageFormDetails, "flex">
  | Omit<CalculEditorFormDetails, "flex">
  | Omit<CalculEditorSimulationFormDetails, "flex">
  | Omit<StaticSelectFormDetails, "flex">
  | Omit<FieldGroupColumnFormDetails, "flex">
  | Omit<WarningFormDetails, "flex">
  | Omit<RadioGroupFormDetails, "flex">
  | Omit<RemoteLookupFormDetails, "flex">
  | Omit<ChartFormDetails, "flex">;

type StaticSelector =
  // `SELECTORS_OPTIONS` keys usable as a form field's `secondaryKey`, i.e. "read
  // the options from this vocabulary rather than from the field's own name".
  | "event_master_impact"
  | "event_master_category"
  | "event_master_scope"
  | "event_master_country"
  | "event_master_is_active"
  | "sweep_priority"
  | "sweep_interest_rating"
  | "sweep_status"
  | "sweep_connector_status"
  | "sweep_rating_lock"
  | "sweep_match"
  | "sweep_contract_state"
  | "sweep_competitor_state"
  | "sweep_coordinates"
  | "sweep_flag"
  | "calculation_type"
  | "calculation_method"
  | "rounding_direction"
  | "rounding_direction_v2"
  | "lever_type"
  | "lever_name"
  | "block_name"
  | "granularity"
  | "industry"
  | "constants_name"
  | "commission_mechanism";

export type EditFormDetail = Omit<EditFormDetails, "label" | "type">;

export enum ConfigurationTableFilterType {
  Select = "select",
  SelectMultiple = "select-multiple",
  SelectMultipleApi = "select-multiple-api",
  Search = "search",
  StaticSelect = "static-select",
  DatePicker = "date-picker",
  ResetButton = "reset-button",
  Unmapped = "unmapped",
  Unlocated = "unlocated",
  WithPastDate = "with-past-date",
  Duplicate = "duplicate",
  DayOfWeek = "day-of-week",
}

export interface FilterDatePicker {
  type: ConfigurationTableFilterType.DatePicker;
  lookup: string;
  label: string;
  // minDateField and maxDateField are used to define a limit date depending wich field is set
  minDateField?: string;
  maxDateField?: string;
  // Allow to choose calendar view (see: https://mui.com/x/react-date-pickers/date-picker/#views)
  views?: DateView[];
  // If true, the filter will be initialized with today's date on load
  defaultToday?: boolean;
}

export interface FilterDayOfWeek {
  type: ConfigurationTableFilterType.DayOfWeek;
  lookup: string;
  label: string;
}

export interface FilterResetButton {
  type: ConfigurationTableFilterType.ResetButton;
}

export type FilterUnmapped = {
  type: ConfigurationTableFilterType.Unmapped;
  api: string;
};

export type FilterWithPastDate = {
  type: ConfigurationTableFilterType.WithPastDate;
  /** Count route, returning `{ count }`. */
  api: string;
};

export type FilterUnlocated = {
  type: ConfigurationTableFilterType.Unlocated;
  /** Count route. Unlike the unmapped filter this is a dedicated action, so it
   *  returns `{ count }` without serialising a page of rows to get there. */
  api: string;
};

export type FilterDuplicate = {
  type: ConfigurationTableFilterType.Duplicate;
  api: string;
};

export type FilterSearch = {
  type: ConfigurationTableFilterType.Search;
  lookup?: string;
};

interface FilterSelector {
  // API to get the options of a select
  api: string;
  apiParams?: {
    page_size: number;
    ordering: string;
    property?: {
      path: string;
    };
    is_staff?: boolean;
  };
  key: string;
  label: string;
  // key to access the label to display when fetching from the API
  labelKey: string;
  // query param to add to the api call to filter by this field
  lookup: string;
  // Use this param to watch other filter, use in case of uhcat filter wich depending on property filter
  watchLookup?: string;
  // Get and use current filter data to change table params when rankable feature is activated
  usefulForRankability?: boolean;
  // Choose between id or label value to use in api query params
  // (In selector filter we format data to have {id: string, label: string} as value, by default: "id" is used in api params)
  useLabelValueForLookup?: boolean;
  // Allow to select empty value
  allowEmpty?: boolean;
  // Set to true when option ids are numeric — URL values will be coerced to Number on restore.
  // Defaults to false: ids are preserved as strings (safe for string-backed and leading-zero codes).
  numericIds?: boolean;
}

export interface FilterSelect extends FilterSelector {
  type: ConfigurationTableFilterType.Select;
}

export interface FilterStaticSelect
  extends Omit<FilterSelector, "api" | "labelKey"> {
  type: ConfigurationTableFilterType.StaticSelect;
}

export interface FilterSelectMultiple extends FilterSelector {
  type: ConfigurationTableFilterType.SelectMultiple;
  mustTranslateOptions: boolean;
  renderOption?: {
    // Which renderOption to use
    type: "color";
    // Use if some data are necessary for the renderOption
    key: string;
  };
  // if multiple is true, decide how many tag can be displayed
  maxTag?: number;
  // If true, the size of input is controlled to avoid increasing its size in case of multiple selections
  // therefore only one tag will be displayed
  controlledInputSize?: boolean;
}

export interface FilterSelectMultipleApi extends FilterSelector {
  type: ConfigurationTableFilterType.SelectMultipleApi;
  // Needed when "type" is "ConfigurationFormType.MultiSelect", used to load multi api in single select according other field input value
  multiApi: MultiApitype;
  // if multiple is true, decide how many tag can be displayed
  maxTag?: number;
  // If true, the size of input is controlled to avoid increasing its size in case of multiple selections
  // therefore only one tag will be displayed
  controlledInputSize?: boolean;
}

export type ConfigurationTableFilter =
  | FilterDatePicker
  | FilterDayOfWeek
  | FilterResetButton
  | FilterSearch
  | FilterSelect
  | FilterStaticSelect
  | FilterSelectMultiple
  | FilterUnmapped
  | FilterUnlocated
  | FilterWithPastDate
  | FilterDuplicate
  | FilterSelectMultipleApi;

export enum RuleTypeType {
  TRANSLATION = "translation",
  TRANSLATION_MAP = "translation-map",
}

// Use this format to translate column according other field value
type RuleTypeTranslation = {
  column: string;
  type: RuleTypeType.TRANSLATION;
};

type RuleTypeTranslationMap = {
  column: string;
  mapTranslation: Record<string, string>;
  type: RuleTypeType.TRANSLATION_MAP;
};

// Use column when you want compare value of a column
type RuleTypeCompareColumn = {
  type: string;
  column: string;
  value: number | boolean;
  operator: string;
  options: {
    text?: string;
    color?: string;
  };
};

type RuleTypeBase = {
  type: string;
  value: number | boolean;
  operator: string;
  options: {
    text?: string;
    color?: string;
  };
};

export type RuleType =
  | RuleTypeBase
  | RuleTypeCompareColumn
  | RuleTypeTranslation
  | RuleTypeTranslationMap;

type ConfigurationTableModelAttributesType = {
  align?: GridAlignment;
  className?: string | ((params: GridCellParams) => string);
  // Use this if column is type CALCUL_EDITOR, use to translate variables
  editorInputs?: {
    value: string;
    label: string;
    isVariable: true;
  }[];
  // Used with type Date, needed for specific date format
  format?: DateTimeFormatOptions;
  headerClassName?: string;
  // Translation key of the tooltip shown when hovering the column header
  headerTooltip?: string;
  // Column is hideable
  hideable?: boolean;
  // Used with Progress type to used value directly as a ratio
  isPercentValue?: boolean;
  // includedIndustries allow to render column for specified industries only
  includedIndustries?: Industry[];
  key: string;
  label: string;
  maximumFractionDigits?: number;
  // If set to true, the option will be translated.
  mustTranslateOptions?: boolean;
  // Used to display a specific number of items in a column when key is an array (ex: properties[].name)
  numberOfColumnItemsToDisplay?: number;
  order: number;
  rules?: RuleType[];
  // Used with type Date, when you need to display the date to a very specific format like "dim. 23 août 2020" => "ccc d LLLL yyyy"
  specificFormat?: string;
  // Set to false when the API cannot order on this column
  sortable?: boolean;
  sortingOrder?: GridSortDirection[];
  // Use sortField if the key of your column is not the name to use for sorting data
  sortField?: string;
  // Used with Progress type for tooltip label
  tooltipLabel?: string;
  // Use to add a tooltip with the cell value (TextCell, ListCell)
  tooltip?: boolean;
  // Used with type ENUM: prefix of the translation key, e.g. "EVENT_MASTER_CATEGORY_"
  translationBaseKey?: string;
  type?: FormatType;
  visible?: boolean;
  // Column width
  width?: number;
  // Field to watch
  watchColumn?: string;
  // Use to determine wich value get in object
  labelKey?: string;
  /**
   * apisFieldMap: Used to generate the initial field values.
   * Acts as a bridge between the watched field value (watchFilter)
   * and the key to read in the API response.
   *
   * The "key" represents the watched field value,
   * and the "value" with {key = represents the property name to extract from the API data, labelKey = the property name use to display data}.
   */
  apisFieldMap?: Record<string, { key: string; labelKey: string } | null>;
}[];

export type ConfigurationTableAction =
  | {
      type: "trigger";
      api: string;
      label: string;
      data?: Record<string, unknown>;
      /**
       * Sends the grid's selected row ids under `key`, merged into `data`. With
       * nothing selected the list is empty (the backend reads that as "all") and
       * the button keeps `label`; otherwise `selectedLabel` is shown, given
       * `{count}`.
       */
      selection?: { key: string; selectedLabel: string };
      /** Intl key wrapping the resolved label, given as `{label}` — e.g. a DEBUG variant. */
      labelWrapper?: string;
      requiredRole?: Roles;
      /** Hidden unless this tenant flag is on — same contract as a page's. */
      flagRequired?: keyof Flags | { flag: keyof Flags; value: string };
    }
  | {
      type: "link";
      href: string;
      label: string;
      requiredRole?: Roles;
      /** Hidden unless this tenant flag is on — same contract as a page's. */
      flagRequired?: keyof Flags | { flag: keyof Flags; value: string };
    }
  // Navigates inside the app, where `link` renders an `<a target="_blank">` and so
  // would reload the whole SPA and lose the session's client-side state.
  | {
      type: "route";
      to: string;
      label: string;
      requiredRole?: Roles;
      /** Hidden unless this tenant flag is on — same contract as a page's. */
      flagRequired?: keyof Flags | { flag: keyof Flags; value: string };
      /** Leading icon, from `ACTION_ICONS` in `toolbarButtons/CustomActions`. */
      icon?: ConfigurationActionIcon;
      /**
       * `"accent"` sets the button apart from the toolbar's usual Create / Export /
       * Import row. For an action that opens somewhere else rather than acting on
       * the table — otherwise it reads as one more operation on the current rows.
       */
      emphasis?: "standard" | "accent";
    };

/** Icons a configuration action may show, kept as names so a `.ts` conf holds no JSX. */
export enum ConfigurationActionIcon {
  SUGGESTIONS = "suggestions",
}

export type ConfigurationTableModel = {
  // Use to change selector value according value in other selector (property => uhcat)
  dynamicPath?: boolean;
  // entityName represent path of object to get the current element name
  entityName: string;
  // enable table menu in action column to manage hide/visble columns
  enableTableColumnMenu?: boolean;
  // path of api
  path: string;
  // path of api preview
  // path of bulk update api
  bulkPath?: string;
  /**
   * Translation key naming the entity, e.g. `PRODUCT`.
   *
   * Required: `useCreateTitle` formats it as the `{model}` of "Créer {model}" and
   * "Modifier {model}: {name}", unconditionally and in five places. Leaving it out
   * throws `[@formatjs/intl] An 'id' must be provided to format a message` the
   * moment a create or edit dialog renders — which is what it used to do, since the
   * field was typed optional while every one of the 76 real configs set it.
   */
  label: string;
  /**
   * Optional subtitle built from the edited row.
   * - forWichDialogType: wich type of dialog the subtitle will be displayed (ConfigurationDialogFormType)
   * - label: String or function to get the subtitle according row data and intl
   */
  subtitle?: {
    dialogFormTypes: ConfigurationDialogFormType[] | "all";
    label: string | ((row: GridRowModel, intl: IntlShape) => ReactNode);
  };
  // queryParams is used for put params in body of request
  queryParams?: { [key: string]: string };
  // queryParamsUrl is used to concat params in request url
  queryParamsUrl?: { [key: string]: string };
  name: string;
  attributes: ConfigurationTableModelAttributesType;
  filters: ConfigurationTableFilter[];
  // TODO: set as mandatory or move to options
  export?: {
    path: string;
  };
  import?: {
    path: string;
  };
  // Custom actions available on top-left of the table
  actions?: ConfigurationTableAction[];
};

export interface ConfigurationPageTable {
  /** Marks the menu entry Beta. Declared on every page shape so the flag
   * is a property of a page rather than of one page type. */
  isBeta?: boolean;

  count?: number;
  description: string;
  flagDisabled?: keyof Flags; // Display the link if condition is met
  flagRequired?: keyof Flags | { flag: keyof Flags; value: string };
  isStaff?: boolean;
  label: string;
  menuLabel: string;
  model: ConfigurationTableModel;
  /**
   * The name can be defined to allow non tab pages to display
   * the unmapped number in the navigation menu
   */
  name?: string;
  options: TableOptions;
  path: string;
  pageLabel: string;
  // Add separator between tabs of tables
  separator?: boolean;
  hideInMenu?: boolean;
  includedCustomers?: string[];
  type: ConfigurationPageType.TABLE;
}

export type ConfigurationPageTables = Omit<
  ConfigurationPageTable,
  "menuLabel" | "pageLabel"
>;

/**
 * A tab that renders its own component instead of a configuration table.
 *
 * ⚠️ For a page that needs behaviour the shared table cannot express — a per-row
 * action, a form built from a schema the server sends, a modal that spends money —
 * *and* still belongs beside its siblings. The alternative is a separate route, which
 * costs the reader a second place to look and a second menu entry for one dataset.
 *
 * It deliberately carries no `model` or `options`: a custom tab owns its own fetching
 * and its own layout, and giving it half a table's configuration would invite the
 * question of which half applies. Everything here is what `MultiTabConfigurationTable`
 * itself reads — the label, the flags, the separator — so the tab strip treats it
 * exactly like any other tab.
 */
export interface ConfigurationPageCustomTab {
  type: ConfigurationPageType.CUSTOM;
  component: ComponentType;
  label: string;
  path: string;
  description?: string;
  count?: number;
  isStaff?: boolean;
  flagDisabled?: keyof Flags;
  flagRequired?: keyof Flags | { flag: keyof Flags; value: string };
  separator?: boolean;
  hideInMenu?: boolean;
  includedCustomers?: string[];
}

/** What one entry of a `TABS` page may be. */
export type ConfigurationTabOption =
  | ConfigurationPageTables
  | ConfigurationPageCustomTab;

/**
 * The tab at `index`, but only if it is a table.
 *
 * ⚠️ For the handful of configs that reach into another page's tabs to reuse one as a
 * `subCreationForm`. They index a position — `tabs[0]` — which was already an
 * assumption about ordering that nothing checked; now that a tab can also be a
 * component, it is an assumption about *type* too. Returning `undefined` rather than
 * asserting means a reordered page loses a sub-form instead of crashing on
 * `options.formFields` of a component.
 */
export const tableTabAt = (
  tabs: ConfigurationTabOption[],
  index: number
): ConfigurationPageTables | undefined => {
  const tab = tabs[index];
  return tab && tab.type === ConfigurationPageType.TABLE ? tab : undefined;
};

export interface ConfigurationPageTab {
  /** Marks the menu entry Beta. Declared on every page shape so the flag
   * is a property of a page rather than of one page type. */
  isBeta?: boolean;

  type: ConfigurationPageType.TABS;
  tabs: ConfigurationTabOption[];
  description?: string;
  count?: number;
  isStaff?: boolean;
  flagDisabled?: keyof Flags; // Display the link if condition is met
  flagRequired?: keyof Flags;
  /**
   * The name must be defined in order to display the unmapped
   * in the navigation menu
   */
  name: string;
  label: string;
  menuLabel: string;
  pageLabel: string;
  pageDescription?: string;
  options?: TableOptions;
  hideInMenu?: boolean;
  includedCustomers?: string[];
  path: string;
}

export interface ConfigurationPageConnectivity {
  /** Marks the menu entry Beta. Declared on every page shape so the flag
   * is a property of a page rather than of one page type. */
  isBeta?: boolean;

  type: ConfigurationPageType.CONNECTIVITY;
  model: ConfigurationTableModel;
  description: string;
  menuLabel: string;
  path: string;
  label: string;
  isStaff?: boolean;
  flagDisabled?: keyof Flags; // Display the link if condition is met
  flagRequired?: keyof Flags;
  name?: string;
  hideInMenu?: boolean;
  includedCustomers?: string[];
}

export interface ConfigurationPageCustom {
  /** Marks the menu entry Beta. Declared on every page shape so the flag
   * is a property of a page rather than of one page type. */
  isBeta?: boolean;

  type: ConfigurationPageType.CUSTOM;
  component: React.ComponentType;
  menuLabel: string;
  path: string;
  label: string;
  description?: string;
  isStaff?: boolean;
  flagDisabled?: keyof Flags;
  flagRequired?: keyof Flags;
  hideInMenu?: boolean;
  name?: string;
  includedCustomers?: string[];
}

export interface ConfigurationPageModelConstruction {
  /** Marks the menu entry Beta. Declared on every page shape so the flag
   * is a property of a page rather than of one page type. */
  isBeta?: boolean;

  type: ConfigurationPageType.MODEL_CONSTRUCTION;
  description: string;
  menuLabel: string;
  path: string;
  label: string;
  isStaff?: boolean;
  flagDisabled?: keyof Flags;
  flagRequired?: keyof Flags;
  name?: string;
  hideInMenu?: boolean;
  includedCustomers?: string[];
}

export type ConfigurationPage =
  | ConfigurationPageTable
  | ConfigurationPageTab
  | ConfigurationPageConnectivity
  | ConfigurationPageCustom
  | ConfigurationPageModelConstruction;

export type ConfigurationSectionType = {
  path: string;
  label: string;
  pages: ConfigurationPage[];
};

export enum ConfigurationFormType {
  AutoFilledInput = "autofilled-input",
  BusinessRuleAction = "business-rule-action",
  BusinessRuleCondition = "business-rule-condition",
  BusinessRulePerimeter = "business-rule-perimeter",
  CalculEditor = "calcul-editor",
  CalculEditorSimulation = "calcul-editor-simulation",
  Chart = "chart",
  Check = "check",
  FieldGroupColumn = "field-group-column",
  DatePicker = "date-picker",
  DateRangePicker = "date-range-picker",
  DayOfWeek = "day-of-week",
  DragDrop = "drag-drop",
  ErrorMessage = "error-message",
  FieldGroup = "field-group",
  Hidden = "hidden",
  Input = "input",
  MultiSelect = "multi-select",
  Preview = "preview",
  PreviewBlock = "preview-block",
  RadioGroup = "radio-group",
  RemoteLookup = "remote-lookup",
  Select = "select",
  SelectStars = "select-stars",
  /** SelectValues
      has been made for Administration
      and its purpose is to propose a widget to
      select multiple values inside an <select> element
  **/
  SelectValues = "select-values",
  Separator = "separator",
  StaticSelect = "static-selector",
  Text = "text",
  TextArea = "textArea",
  TextToggled = "textToggled",
  ToggleSection = "toggle-section",
  WeekDaysRounded = "week-days-rounded",
  Warning = "warning",
}

export interface SelectorOption {
  key: string;
  label: string;
}

// Options for static slector component
export enum SelectorCalculationType {
  ADDITION = "Ajout",
  SUBTRACTION = "Soustraction",
}

// Options for static slector component
export enum SelectorCommissionMechanismType {
  ABSOLUTE = "absolute",
  RELATIVE = "relative",
}

// Options for static slector component
export enum SelectorCalculationMethodType {
  VALUE = "Valeur",
  PERCENTAGE = "Pourcentage",
}

// Options for static selector component
export enum SelectorKpiFormatType {
  KPI_FORMAT_NUMBER = "number",
  KPI_FORMAT_NUMBER_SIGNED = "number_signed",
  KPI_FORMAT_PERCENT_0 = "percent_0",
  KPI_FORMAT_PERCENT_0_SIGNED = "percent_0_signed",
  KPI_FORMAT_CURRENCY_0 = "currency_0",
  KPI_FORMAT_CURRENCY_1 = "currency_1",
  KPI_FORMAT_CURRENCY_2 = "currency_2",
  KPI_FORMAT_CURRENCY_0_SIGNED = "currency_0_signed",
  KPI_FORMAT_CURRENCY_1_SIGNED = "currency_1_signed",
  KPI_FORMAT_CURRENCY_2_SIGNED = "currency_2_signed",
}

// Options for static slector component
export enum SelectorRoundingDirectionType {
  UP = "Supérieur",
  DOWN = "Inférieur",
}

export enum SelectorRoundingDirectionTypeV2 {
  UP = "Up",
  DOWN = "Down",
}

export enum SelectorStatusType {
  DRAFT = "draft",
  ACCEPTED = "accepted",
  REJECTED = "rejected",
  CANCELED = "canceled",
}

// Options for static slector component
export enum SelectorLeverTypeType {
  TARIF = "tarif",
  YIELD_CLASS = "classe_tarif",
  SURBOOK = "surbook",
  MIN_STAY_CONF = "min_stay",
}

// Options for static slector component
export enum SelectorLeverNameType {
  PRICE_UP = "prix_montee",
  PRICE_DOWN = "prix_baisse",
  YC_CLOSURE_CONF = "classe_tarif_fermeture",
  YC_OPENING_CONF = "classe_tarif_ouverture",
  SURBOOKING_REMOVE = "surbooking_retrait",
  SURBOOKING_ADD = "surbooking_ajout",
  MIN_STAY_LS_ADD = "min_stay_ls_ajout",
  MIN_STAY_LS_REMOVE = "min_stay_ls_retrait",
  MIN_STAY_CS_ADD = "min_stay_cs_ajout",
  MIN_STAY_CS_REMOVE = "min_stay_cs_retrait",
}

// Options for static slector component
export enum SelectorConstantType {
  MODEL_GRAIN = "grain_modeles",
  RECO_SURBOOK = "surquota_reco_o_f",
  LIMIT_BOOKING_TYPE = "type_booking_limit",
  CAPA_BY_DAY = "capacite_couchee_par_jour",
  GAUGE_MIN_VALUE = "nb_min_value_jauge",
  IS_ACTIVE_BUY_UP = "isactive_default_buyup",
  PAST_YEAR_MAPPINH = "nb_past_year_mapping_qv",
  START_DATE_LAERNING = "ml_date_debut_apprentissage",
  END_DATE_LEARNING = "ml_date_fin_apprentissage",
  ASYMMETRY_CAPA_MIN = "asymetrie_capa_min",
}

// Options for static slector component
export enum SelectorBlockNameType {
  BUDGET_LANDING = "ATTERRISSAGE BUDGETAIRE",
  AUTOPILOT = "AUTOPILOT",
  SALES_STATUS = "ETAT DES VENTES",
  PICKUP_LAST_SEVEN_DAYS = "PICK UP DE 7 DERNIERS JOURS",
  PREVISION_MENSUELLE = "PREVISION MENSUELLE",
  SEGMENTATION_CANAL = "SEGMENTATION CANAL",
  SEGMENTATION_CANAL_GROUPE = "SEGMENTATION CANAL GROUPE",
  SEGMENTATION_CANAL_GROUPE_MASTER = "SEGMENTATION CANAL GROUPE MASTER",
  SEGMENTATION_CLASSE_TARIFAIRE = "SEGMENTATION CLASSE TARIFAIRE",
  SEGMENTATION_PAYS_GROUPE = "SEGMENTATION PAYS GROUPE",
  SEGMENTATION_PAYS_GROUPE_MASTER = "SEGMENTATION PAYS GROUPE MASTER",
  SEGMENTATION_SEGMENT = "SEGMENTATION SEGMENT",
  SEGMENTATION_SEGMENT_GROUPE = "SEGMENTATION SEGMENT GROUPE",
  SEGMENTATION_SEGMENT_GROUPE_MASTER = "SEGMENTATION SEGMENT GROUPE MASTER",
  SEGMENTATION_TYPE_CLIENT = "SEGMENTATION TYPE CLIENT",
  TABLEAU_HEBDOMADAIRE = "TABLEAU HEBDOMADAIRE",
  TABLEAU_MENSUEL = "TABLEAU MENSUEL",
  TOP_10_ALERTES = "TOP 10 ALERTES",
}

// Options for static slector component
/**
 * Mirrors `RADIUS_CHOICES` on the backend (5 to 40 km in 5 km steps). The API
 * validates against the same list, so a value missing here is simply unreachable
 * from the form rather than rejected on save.
 */
export const SelectorRadiusOfInterestType = {
  RADIUS_5_KM: 5,
  RADIUS_10_KM: 10,
  RADIUS_15_KM: 15,
  RADIUS_20_KM: 20,
  RADIUS_25_KM: 25,
  RADIUS_30_KM: 30,
  RADIUS_35_KM: 35,
  RADIUS_40_KM: 40,
};

export enum SelectorAreaType {
  ALL = "ALL",
  GEO_AREA_1 = "ZONE_GEO_1",
  GEO_AREA_2 = "ZONE_GEO_2",
  PROPERTY = "PROPERTY",
}

// Options for static slector component
export enum SelectorIndustryType {
  PROPERTY = "Hôtellerie",
  CAMPING = "Camping",
  PARK = "Parc",
  BOAT = "Bateau",
}

// Options for static slector component
export enum SelectorTypeActionType {
  PRICE_INCREASE = "hausse_tarif",
  NOTHING = "rien",
  GAUGE_POSITION = "position_jauge",
}

// Options for static slector component
/**
 * `EventMaster.impact` — editorial popularity.
 *
 * ⚠️ **Not** `SelectorTypeImpactType`, which is `Event.impact`: that one is
 * *directional* (does the event fill or empty the property) and shares nothing but
 * the column name. Registered under its own `SELECTORS_OPTIONS` key so a form
 * keyed on `impact` cannot silently offer the wrong vocabulary — the backend
 * would reject every one of those values.
 */
export enum SelectorEventMasterImpactType {
  EVENT_MASTER_IMPACT_EXTREMELY_HIGH = "EXTREMELY_HIGH",
  EVENT_MASTER_IMPACT_HIGH = "HIGH",
  EVENT_MASTER_IMPACT_MEDIUM = "MEDIUM",
  EVENT_MASTER_IMPACT_LOW = "LOW",
  EVENT_MASTER_IMPACT_UNDEFINED = "UNDEFINED",
}

export enum SelectorEventMasterCategoryType {
  EVENT_MASTER_CATEGORY_TRADE_SHOW = "trade_show",
  EVENT_MASTER_CATEGORY_CONCERT = "concert",
  EVENT_MASTER_CATEGORY_SPORT = "sport",
  EVENT_MASTER_CATEGORY_FESTIVAL = "festival",
  EVENT_MASTER_CATEGORY_PUBLIC_HOLIDAY = "public_holiday",
  EVENT_MASTER_CATEGORY_SCHOOL_HOLIDAY = "school_holiday",
  EVENT_MASTER_CATEGORY_OTHER = "other",
}

/** Reaches the `is_active` filter as the string django-filter expects. */
export enum SelectorEventMasterActiveType {
  EVENT_MASTER_ACTIVE_ONLY = "true",
  EVENT_MASTER_INACTIVE_ONLY = "false",
}

export enum SelectorEventMasterScopeType {
  EVENT_MASTER_SCOPE_POINT = "point",
  EVENT_MASTER_SCOPE_REGION = "region",
  EVENT_MASTER_SCOPE_COUNTRY = "country",
}

/**
 * The countries revbell has connectors for, mirroring `SUPPORTED_COUNTRY_CODES`
 * backend-side.
 *
 * A picker rather than a free-text input because the value is compared verbatim:
 * an event stored as `fr` matches no property, and nothing about the saved row
 * would say so. Uppercase because that is the one casing the backend stores.
 */
export enum SelectorEventMasterCountryType {
  EVENT_MASTER_COUNTRY_FR = "FR",
  EVENT_MASTER_COUNTRY_ES = "ES",
}

/** `EventVenue.match_level` — how precisely the coordinates pin the venue. */
export enum SelectorEventVenueMatchLevelType {
  EVENT_VENUE_MATCH_LEVEL_VENUE = "venue",
  EVENT_VENUE_MATCH_LEVEL_CITY = "city",
  EVENT_VENUE_MATCH_LEVEL_OVERRIDE = "override",
}

/** `?status=` on the venue cache — see `EventVenueFilter` backend-side. */
export enum SelectorEventVenueStatusType {
  EVENT_VENUE_STATUS_GIVEN_UP = "given_up",
  EVENT_VENUE_STATUS_RETRYING = "retrying",
  EVENT_VENUE_STATUS_RESOLVED = "resolved",
}

/**
 * The sweep's own vocabularies (COMPETIT-HIVE admin page).
 *
 * Values are what reaches the API verbatim, so the integer scales are quoted: DRF's
 * `ChoiceField` maps `"3"` back to `3`, and a filter query param is a string anyway.
 *
 * `family` deliberately has **no** enum. It comes from
 * `/rbl/connector-configurations/families/`, which serves the connector registry — a family that
 * is not a registered connector class cannot exist, so a hardcoded list here could only
 * ever be a copy that drifts.
 */
export enum SelectorSweepPriorityType {
  COMPETITOR_PRIORITY_0 = "0",
  COMPETITOR_PRIORITY_1 = "1",
  COMPETITOR_PRIORITY_2 = "2",
  COMPETITOR_PRIORITY_3 = "3",
  COMPETITOR_PRIORITY_4 = "4",
  COMPETITOR_PRIORITY_5 = "5",
}

export enum SelectorSweepInterestType {
  COMPETITOR_INTEREST_0 = "0",
  COMPETITOR_INTEREST_1 = "1",
  COMPETITOR_INTEREST_2 = "2",
  COMPETITOR_INTEREST_3 = "3",
  COMPETITOR_INTEREST_4 = "4",
  COMPETITOR_INTEREST_5 = "5",
}

export enum SelectorSweepStatusType {
  COMPETITOR_STATUS_ENABLED = "true",
  COMPETITOR_STATUS_DISABLED = "false",
}

/**
 * ⚠️ Beside `SelectorSweepStatusType`, not instead of it. That one filters
 * `is_active`, the on/off gate the sweep reads; this filters `status`, where the
 * connector is in its life. A BROKEN connector is normally still active — we want it to keep
 * retrying — so one cannot be derived from the other.
 */
export enum SelectorSweepConnectorStatusType {
  COMPETITOR_CONNECTOR_STATUS_OK = "OK",
  COMPETITOR_CONNECTOR_STATUS_DISABLED = "DISABLED",
  COMPETITOR_CONNECTOR_STATUS_BROKEN = "BROKEN",
  COMPETITOR_CONNECTOR_STATUS_IN_CREATION = "IN_CREATION",
  COMPETITOR_CONNECTOR_STATUS_WAITING_FOR_REVIEW = "WAITING_FOR_REVIEW",
}

export enum SelectorSweepLockType {
  COMPETITOR_RATING_MANUAL = "true",
  COMPETITOR_RATING_DERIVED = "false",
}

/**
 * `?<fk>__isnull=`, phrased as the question it answers: a source row with no
 * canonical row behind it is a property the sweep prices and cannot attribute.
 */
/**
 * `?state=`. A contract's lifecycle, which replaced the old `is_active` boolean.
 *
 * ⚠️ **Three states keep a property off the client's page.** `DISABLED` ends the
 * contract; `DISCOVERING` and `NO_COMPETITORS` would open it on an empty map. The
 * rest are live — `MATCHING_FAILED` gates only the customer's own property being
 * tracked, and `NO_LOCATION` shows the page's fix-the-location prompt — so
 * filtering for `READY` alone would hide most working contracts.
 */
export enum SelectorSweepContractStateType {
  COMPETITOR_CONTRACT_DISCOVERING = "DISCOVERING",
  COMPETITOR_CONTRACT_NO_COMPETITORS = "NO_COMPETITORS",
  COMPETITOR_CONTRACT_NO_LOCATION = "NO_LOCATION",
  COMPETITOR_CONTRACT_IN_CREATION = "IN_CREATION",
  COMPETITOR_CONTRACT_MATCHING_FAILED = "MATCHING_FAILED",
  COMPETITOR_CONTRACT_READY = "READY",
  COMPETITOR_CONTRACT_DISABLED = "DISABLED",
}

/** `Competitor.state`. `DISABLED` keeps the competitor out of the selection page's search. */
export enum SelectorSweepCompetitorStateType {
  COMPETITOR_STATE_OK = "OK",
  COMPETITOR_STATE_DISABLED = "DISABLED",
}

export enum SelectorSweepMatchType {
  COMPETITOR_UNMATCHED = "true",
  COMPETITOR_MATCHED = "false",
}

/**
 * `?latitude__isnull=`. A competitor with no coordinates can never match a radius,
 * so it is invisible to `get_competitors` however many prices it has — see §7.7 of
 * the sweep TODO. This filter is how you find them.
 */
export enum SelectorSweepCoordinatesType {
  COMPETITOR_WITHOUT_COORDINATES = "true",
  COMPETITOR_WITH_COORDINATES = "false",
}

/**
 * `?<flag>__isnull=` on a rate's condition flags. A connector reports them only when the
 * provider says so plainly, so "missing" is the actionable state — the name of the
 * plan usually makes the answer obvious to a human.
 */
export enum SelectorSweepFlagType {
  COMPETITOR_FLAG_MISSING = "true",
  COMPETITOR_FLAG_SET = "false",
}

export enum SelectorTypeImpactType {
  EVENT_IMPACT_FILLING_HIGH = "FILLING HIGH",
  EVENT_IMPACT_FILLING_LOW = "FILLING LOW",
  EVENT_IMPACT_NEUTRAL = "NEUTRAL",
  EVENT_IMPACT_EMPTYING_LOW = "EMPTYING LOW",
  EVENT_IMPACT_EMPTYING_HIGH = "EMPTYING HIGH",
  EVENT_IMPACT_UNDEFINED = "UNDEFINED",
}

// Options for static slector component
export enum SelectorTypeAncillaryUhcatType {
  QUOTATION_GROUP_BREAKFAST = "breakfast",
  QUOTATION_GROUP_LUNCH = "lunch",
  QUOTATION_GROUP_DINNER = "dinner",
  QUOTATION_GROUP_ACTIVITIES = "activities",
  QUOTATION_GROUP_HOUSEKEEPING = "housekeeping",
  QUOTATION_GROUP_OTHER = "other",
}

// Options for static slector component
export enum SelectorTypeAncillaryGroupType {
  QUOTATION_GROUP_RENT = "rent",
  QUOTATION_GROUP_GUIDE = "guide",
  QUOTATION_GROUP_ANIMATION = "animation",
  QUOTATION_GROUP_TOURS = "tours",
  QUOTATION_GROUP_OTHER = "other",
}

export type Item = { id: number } & Record<string, any>;
export type Option = {
  id: number | string;
  label: string;
  key?: string | null;
  labelKey?: string;
  isHardCodedOption?: boolean;
  [key: string]: any;
};
export type OptionList = Array<Option>;

export enum ConfigurationDialogFormType {
  CREATE = "create",
  EDIT = "edit",
  MASS_EDIT = "mass_edit",
  FREE = "free",
}

export type FormDataType = {
  initialValues?: GenericObject;
  model: ConfigurationTableModel;
  options: FormDetailsType;
  row?: GridRowModel;
};

export type filterType =
  | EditFormDetails
  | MultiApiTypeFormDetails
  | DateRangePickerFormDetails
  | SelectFilterItemSortedFormDetails
  | FieldGroupFormDetails
  | PreviewsBlockFormDetails
  | ErrorMessageFormDetails
  | CalculEditorFormDetails
  | CalculEditorSimulationFormDetails
  | FieldGroupColumnFormDetails
  | WarningFormDetails
  | RadioGroupFormDetails
  | RemoteLookupFormDetails
  | ChartFormDetails;

export enum ApiResultsType {
  Array = "array",
  ObjectsList = "objectsList",
}

export interface CustomToolbarProps {
  rows: GridRowModel[];
  multipleSelection: GridRowSelectionModel;
  tableModel: ConfigurationTableModel;
  tableOptions?: TableOptions;
  refetch: any;
  handleOpenSnackBar: (success: boolean, data?: Response) => void;
  handleRanking: () => void;
  handleSaveRanking: () => void;
  handleUnselectRows: () => void;
  rowReorderingActive: boolean;
  params: GenericObject;
  rawFilter: GenericObject;
  isLoading: boolean;
  rowCount: number;
}

// Options for lead time override
export enum SelectorLeadTimeOverrideType {
  LT_MIN = "lt_min",
  LT_MAX = "lt_max",
  LT_REEL = "lt_reel",
}
